// App.js
import React, { useState, useContext, useRef } from 'react';
import Sidebar from './components/App/Sidebar';
import BottomMenu from './components/App/BottomMenu';
import Settings from './components/App/Settings';
import { AuthContext } from './components/Login/AuthContext';
import Editor from './components/App/Editor/Editor';
import CreateProjectForm from './components/NewProject/CreateProjectForm';
import PricingPopup from './components/App/PricingPopup';
import Generator from './components/App/Generator';
import api from './components/api';
import WarningPopup from './components/UI/WarningPopup';
import { ParallaxProvider } from 'react-scroll-parallax';

import './App.css';

function App() {
    const [currentPage, setCurrentPage] = useState('mainApp');
    const [currentProject, setCurrentProject] = useState(null);
    const [isNewProject, setIsNewProject] = useState(false);
    const [isPricingOpen, setIsPricingOpen] = useState(false);
    const [isLoadingProject, setIsLoadingProject] = useState(false);
    const { isAuthenticated } = useContext(AuthContext);
    const sidebarRef = useRef();

    const handleNewProject = () => {
        setCurrentPage('newProject');
    };

    const handleProjectSelected = async (projectId) => {
        try {
            setIsLoadingProject(true);
            const response = await api.get(`/projects/${projectId}`);
            setCurrentProject(response.data);
            setCurrentPage('mainApp');
        } catch (error) {
            console.error('Project GET Error:', error);
        } finally {
            setIsLoadingProject(false);
        }
    };

    const handleOpenSettings = () => {
        setCurrentPage('settings');
    };

    const handleOpenGenerator = () => {
        setCurrentPage('generator');
    };

    const handleProjectCreated = (projectId) => {
        console.log("Project created with ID:", projectId);
        setIsNewProject(true);
        
        // Rafraîchir la liste des projets dans la sidebar
        if (sidebarRef.current) {
            console.log("Refreshing sidebar projects...");
            sidebarRef.current.refreshProjects();
        }
        
        // Attendre un peu que les projets soient chargés avant de sélectionner le nouveau projet
        setTimeout(() => {
            console.log("Selecting newly created project...");
            handleProjectSelected(projectId);
            
            // Rafraîchir à nouveau la sidebar après avoir sélectionné le projet
            if (sidebarRef.current) {
                sidebarRef.current.refreshProjects();
            }
        }, 300);
    };

    const handleProjectDeleted = () => {
        setCurrentProject(null);
        setCurrentPage('mainApp');
        sidebarRef.current?.refreshProjects();
    };

    const handleProjectUpdated = () => {
        sidebarRef.current?.refreshProjects();
    };

    const renderContent = () => {
        if (currentPage === 'newProject') {
            return <CreateProjectForm onProjectCreated={handleProjectCreated} />;
        }

        if (currentPage === 'settings') {
            return <Settings onOpenPricing={() => setIsPricingOpen(true)} />;
        }

        if (currentPage === 'generator') {
            return <Generator onOpenPricing={() => setIsPricingOpen(true)} />;
        }

        if (isLoadingProject) {
            return (
                <div className="loading-container">
                    <div className="loader"></div>
                    <p>Loading the project...</p>
                </div>
            );
        }

        return currentProject ? (
            <Editor 
                key={currentProject.id}
                project={currentProject} 
                isNewProject={isNewProject} 
                onOpenPricing={() => setIsPricingOpen(true)} 
            />
        ) : (
            <div className="empty-state">Select or create a new project to start writing</div>
        );
    };

    return (
        <>
            <div className="container-fluid" style={{ padding: 0 }}>
                {isAuthenticated && (
                    <div className='app'>
                        <Sidebar
                            ref={sidebarRef}
                            onProjectSelected={(projectId) => {
                                setIsNewProject(false);
                                handleProjectSelected(projectId);
                            }}
                            onNewProject={handleNewProject}
                            isNewProject={isNewProject}
                            selectedProject={currentProject}
                            onOpenPricing={() => setIsPricingOpen(true)}
                            onOpenSettings={handleOpenSettings}
                            onOpenGenerator={handleOpenGenerator}
                        />

                        <div className="content-section">
                            {renderContent()}
                        </div>
                        <BottomMenu
                            ref={sidebarRef}
                            onProjectSelected={(projectId) => {
                                setIsNewProject(false);
                                handleProjectSelected(projectId);
                            }}
                            onNewProject={handleNewProject}
                            isNewProject={isNewProject}
                            selectedProject={currentProject}
                            onOpenPricing={() => setIsPricingOpen(true)}
                            onOpenSettings={handleOpenSettings}
                            onOpenGenerator={handleOpenGenerator}
                            selected={currentPage}
                        />

                    </div>
                )}
            </div>

            {isPricingOpen && (
                <PricingPopup onClose={() => setIsPricingOpen(false)} />
            )}

            <WarningPopup popupName="warningPopupDismissed">
                📱 While Book Witch can be used on mobile, we highly recommend using it on a PC for a much smoother experience. Can Microsoft Word run on mobile? Yes. Is it a nightmare to use? Also, yes! 😅<br />
                <br />
                So, grab a coffee, settle in at your desk, and let the AI inspire your next essay. ☕️💻
            </WarningPopup>
        </>
    );
}

export default App;
